package com.sddamico.draftmachine.ds.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import org.w3c.dom.HTMLInputElement
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleMono
import com.sddamico.draftmachine.ds.roleSmall
import com.sddamico.draftmachine.ds.shadowInset
import com.sddamico.draftmachine.ds.transitionRaw
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input as DomInput
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text

enum class InputSize { Sm, Md }

/**
 * A sunken brass-slot text field — a recessed plate milled into the panel, with optional label,
 * hint/error, leading icon and trailing suffix. Port of `components/forms/Input.jsx`.
 */
@Composable
fun Input(
  modifier: Modifier = Modifier,
  label: String? = null,
  hint: String? = null,
  error: String? = null,
  icon: String? = null,
  suffix: String? = null,
  placeholder: String? = null,
  type: String = "text",
  value: String? = null,
  size: InputSize = InputSize.Md,
  id: String? = null,
  autoFocus: Boolean = false,
  onValueChange: ((String) -> Unit)? = null,
  onEnter: (() -> Unit)? = null,
) {
  var focused by remember { mutableStateOf(false) }
  val inputElement = remember { arrayOfNulls<HTMLInputElement>(1) }
  // The label slug is fine for a one-off field, but repeated rows (a roster of teams, say) would
  // all answer to the same id and every label would point at the first row's input — pass [id].
  val fieldId = id ?: slugId("in", label)
  val currentOnValueChange = rememberUpdatedState(onValueChange)
  val currentOnEnter = rememberUpdatedState(onEnter)
  val padX = if (size == InputSize.Sm) DmSpacing.space3 else DmSpacing.space4
  val height = if (size == InputSize.Sm) DmSpacing.controlSm else DmSpacing.controlMd
  val borderColor = when {
    error != null -> DmColor.danger
    focused -> DmColor.brass500
    else -> DmColor.borderStrong
  }
  val shadow = if (focused) "${DmEffects.shadowInset}, ${DmEffects.ring}" else DmEffects.shadowInset

  LaunchedEffect(autoFocus) {
    if (autoFocus) inputElement[0]?.focus()
  }

  Div(
    attrs = modifier
      .displayFlex().flexDirection(FlexDirection.Column).gap(DmSpacing.space2).fillMaxWidth()
      .toAttrs()
  ) {
    if (label != null) {
      Label(
        attrs = Modifier.roleSmall().fontWeight(DmType.weightSemibold).color(DmColor.textSecondary)
          .toAttrs { fieldId?.let { id -> attr("for", id) } }
      ) { Text(label) }
    }
    Div(
      attrs = Modifier
        .displayFlex().alignItems(AlignItems.Center).gap(DmSpacing.space2)
        .height(height).padding(topBottom = 0.px, leftRight = padX)
        .backgroundColor(DmColor.surfaceInset)
        .border(1.px, LineStyle.Solid, borderColor)
        .borderRadius(DmEffects.radiusSm)
        .boxShadowRaw(shadow)
        .transitionRaw("border-color ${DmMotion.durFast}, box-shadow ${DmMotion.durFast}")
        .toAttrs()
    ) {
      if (icon != null) {
        Icon(icon, IconWeight.Regular, Modifier.fontSize(18.px).color(DmColor.textMuted).flex("0 0 auto"))
      }
      DomInput(
        type = InputType.Text,
        attrs = Modifier
          .flex("1").minWidth(0.px).height(100.percent).color(DmColor.text).roleBody()
          .styleModifier { property("background", "transparent"); property("border", "none"); property("outline", "none") }
          .toAttrs {
            fieldId?.let { attr("id", it) }
            attr("type", type)
            if (placeholder != null) attr("placeholder", placeholder)
            onInput { ev -> currentOnValueChange.value?.invoke(ev.value) }
            onKeyDown { ev -> if (ev.key == "Enter") { ev.preventDefault(); currentOnEnter.value?.invoke() } }
            onFocus { focused = true }
            onBlur { focused = false }
            ref { el ->
              val htmlEl = el as HTMLInputElement
              inputElement[0] = htmlEl
              // Set value imperatively only when it differs — avoids resetting cursor on typing.
              if (value != null && value != htmlEl.value) htmlEl.value = value
              onDispose { inputElement[0] = null }
            }
          }
      )
      if (suffix != null) {
        SpanText(suffix, Modifier.roleMono().color(DmColor.textMuted).flex("0 0 auto"))
      }
    }
    if (hint != null || error != null) {
      SpanText(
        error ?: hint!!,
        Modifier.roleSmall().fontSize(DmType.textXs).color(if (error != null) DmColor.danger else DmColor.textMuted)
      )
    }
  }
}

/** Build a stable-ish element id from a label, e.g. "in-league-name". */
internal fun slugId(prefix: String, label: String?): String? =
  label?.let { "$prefix-" + it.trim().lowercase().replace(Regex("\\s+"), "-") }
